import { Address } from "./Address";
import { AttachmentLog } from "./Attachment";
import { Account } from "./Financial";
import { HorseSale, SimpleHorseForSale } from "./HorseForSale";
import { LsysUser } from "./User";

export type CustomerType = "CONTACT" | "BUSINESS";
export type CustomerTypeWithBoth = CustomerType | "BOTH";

export type CreditApplicationType = "PERMANENT" | "ON_THE_SPOT";

// TODO: Return to this and add `|null` to those that are missing it.

export interface SimpleCustomer {
    id: number;
    type: CustomerType;
    referenced: boolean;

    address: Address | null;
    primaryPhone: string;

    email: string;

    dateArchived?: Date;
    dateFlagged?: Date;
    flaggedBy?: number; //corresponds to User id
    flaggedNotes?: string;

    identification: CustomerIdentification;
}

export interface Customer extends SimpleCustomer {
    secondaryPhone: string;
    faxNumber: string;
    firstNationsIdNumber: string | null;

    creditApplications: Array<CreditApplication>;
    agentAuthorizations: Array<AgentAndDelegatorAuthorization>;
    delegatorAuthorizations: Array<AgentAndDelegatorAuthorization>;
    insuranceRecords: Array<InsuranceRecord>;

    notes: string;
    account: Account;
}

export interface Contact extends Customer {
    type: "CONTACT";
    firstName: string;
    middleName: string;
    lastName: string;
    contactFor: Array<ReverseBusinessContactRelationship>;
    involvement: ContactInvolvement;
}

export type BaseInvolvement = { horseForSale: SimpleHorseForSale };
export interface ContactInvolvement {
    ownerFor: Array<
        BaseInvolvement & {
            percentage: number;
        }
    >;
    consignorFor: Array<BaseInvolvement>;
    winningBidderFor: Array<
        BaseInvolvement & {
            finalBidAmount: number;
        }
    >;
    buyerFor: Array<
        BaseInvolvement & {
            percentage: number;
            notes: string;
        }
    >;

    raisedByFor: Array<BaseInvolvement>;
    signatureContactFor: Array<BaseInvolvement>;
    contactForHorses: Array<BaseInvolvement>;
}

export interface Business extends Customer {
    type: "BUSINESS";
    businessName: string;
    contacts: Array<BusinessContactRelationship>;
}

export interface ReverseBusinessContactRelationship {
    title: string;
    business: SimpleCustomer;
}

export interface BusinessContactRelationship {
    title: string;
    contact: SimpleCustomer;
}

export interface CustomerIdentification {
    identificationNumber: string | null;
    typeOfIdentification: CustomerIdentificationType | null;
}

export interface CustomerIdentificationType {
    id: number;
    name: string;
    applicability: CustomerTypeWithBoth;
}

export const FilterToContacts = (customers: Array<SimpleCustomer>): Array<Contact> => {
    return customers.filter(c => c.type === "CONTACT").map(c => c as Contact);
};

export const FilterToBusinesses = (customers: Array<SimpleCustomer>): Array<Business> => {
    return customers.filter(c => c.type === "BUSINESS").map(c => c as Business);
};

export const ExtractFriendlyName = (customer: SimpleCustomer, lastNameFirst: boolean = false): string => {
    if (customer.type === "BUSINESS") {
        return (customer as Business).businessName.trim();
    } else if (customer.type === "CONTACT" && !lastNameFirst) {
        return `${(customer as Contact).firstName} ${(customer as Contact).lastName}`.trim();
    } else if (customer.type === "CONTACT" && lastNameFirst) {
        return `${(customer as Contact).lastName}, ${(customer as Contact).firstName}`.trim();
    }
    return "";
};

export enum CreditApplicationState {
    APPROVED = "APPROVED",
    PENDING = "PENDING",
    REVOKED = "REVOKED",
    REJECTED = "REJECTED"
}

export const CreditApplicationStateFriendlyName = (state: CreditApplicationState): string => {
    switch (state) {
        case CreditApplicationState.APPROVED:
            return "Approved";
        case CreditApplicationState.REVOKED:
            return "Revoked";
        case CreditApplicationState.PENDING:
            return "Pending";
        case CreditApplicationState.REJECTED:
            return "Rejected";
        default:
            return "?";
    }
};

export interface CreditApplication {
    id: number;
    type: CreditApplicationType;
    state: CreditApplicationState;
    dateCreated: number;
    createdBy: LsysUser | null;

    amountRequested: number;

    attachmentLog: AttachmentLog;
    notes: string | null;
}

export interface PermanentCreditApplication extends CreditApplication {
    dateApproved: number | null;
    approvedBy: LsysUser | null;
    dateRejectedRevoked: number | null;
    rejectedRevokedBy: LsysUser | null;
}

export interface OnTheSpotCreditApplication extends CreditApplication {
    validFor: SimpleHorseForSale;
    notes: string;
}

export interface AgentAndDelegatorAuthorization {
    id: number;
    dateCreated: number;

    dateRevoked: number | null;
    revokedNotes: string | null;

    dateSigned: number | null;
    sale: HorseSale;
    agent: SimpleCustomer;
    delegator: SimpleCustomer;

    notes: string;
    attachmentLog: AttachmentLog;
}

export interface InsuranceRecord {
    id: number;
    dateCreated: number;
    dateCancelled: number | null;
    policyInception: Date;
    brokerName: string;
    attachmentLog: AttachmentLog;
    company: InsuranceCompany;
}

export interface InsuranceCompany {
    id: number;
    type: "EXISTING" | "AD_HOC";
    businessName: string;
    contactName: string;
    address: Address | null;
    phone: string | null | undefined;
    email: string | null | undefined;
}



// WEBPACK FOOTER //
// ./src/model/Customer.ts